import { useMemo } from "react";
import Chart from "react-apexcharts";

import { spansMultipleYears } from "~/helpers/formatDate";
import { formatMetricOrDimensionName } from "~/pages/GoogleDashboardPage/utils/formatMetricOrDimensionName";
import { useChartTheme } from "~/pages/GoogleDashboardPage/utils/useChartTheme";

import type {
  GoogleAnalyticsPropertyRealtimeReport,
  GoogleAnalyticsPropertyReport,
} from "../../../../types/RenderableWidget.type";
import {
  formatDimensionValue,
  parseDimensionDate,
} from "../../utils/formatDimensionValue";
import { formatMetricValue } from "../../utils/formatMetricValue";
import styles from "./BarChartView.module.scss";

export const BarChartView = ({
  data,
}: {
  data: GoogleAnalyticsPropertyReport | GoogleAnalyticsPropertyRealtimeReport;
}) => {
  const chartTheme = useChartTheme();

  if (!data.dimension_headers || data.dimension_headers.length === 0) {
    throw new Error("Bar chart requires at least one dimension");
  }

  // Same year-detection trick as LineChartView so date axes stay compact.
  const dateDimensionIdx = data.dimension_headers.findIndex(
    (h) => h.name === "date",
  );
  const showYear =
    dateDimensionIdx === -1
      ? true
      : spansMultipleYears(
          (data.rows || [])
            .map((row) => {
              const value = row.dimension_values?.[dateDimensionIdx]?.value;

              return value ? parseDimensionDate(value) : null;
            })
            .filter((d): d is Date => d !== null),
        );

  const categories = useMemo(
    () =>
      (data.rows || []).map((row) =>
        row.dimension_values
          ?.map(({ value }, idx) => {
            return formatDimensionValue(
              value,
              data.dimension_headers![idx].name,
              showYear,
            );
          })
          .join(" - "),
      ),
    [data.rows, data.dimension_headers, showYear],
  );

  const series = useMemo(
    () =>
      data.metric_headers.map(({ name, type }, idx) => {
        if (!(type in PARSERS)) {
          throw new Error(`Bar chart does not support metrics of ${type} type`);
        }

        return {
          name,
          data: (data.rows || []).map((row) =>
            PARSERS[type](row.metric_values[idx].value),
          ),
        };
      }),
    [data.metric_headers, data.rows],
  );

  // Check if any metrics are float type
  const hasFloatMetrics = data.metric_headers.some(
    ({ type }) => type === "TYPE_FLOAT" || type === "TYPE_PERCENT",
  );

  // When the series count exceeds the palette length, Apex repeats colors
  // automatically. For a single-metric report we want each bar coloured
  // distinctly (matches VE breakdown chart); otherwise we let Apex assign
  // a color per series so the legend keys still make sense.
  const distributed = series.length === 1;

  const options = useMemo(
    () => ({
      chart: {
        type: "bar" as const,
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: chartTheme.fontFamily,
        // See LineChartView for the rationale — initial mount animates,
        // subsequent updates (including resize-observer redraws) don't.
        animations: {
          enabled: true,
          dynamicAnimation: { enabled: false },
        },
      },
      colors: chartTheme.palette,
      plotOptions: {
        bar: {
          borderRadius: 6,
          borderRadiusApplication: "end" as const,
          distributed,
          columnWidth: "55%",
        },
      },
      states: {
        active: { filter: { type: "none" as const } },
        hover: { filter: { type: "none" as const } },
      },
      dataLabels: { enabled: false },
      grid: {
        borderColor: chartTheme.gridBorder,
        strokeDashArray: 4,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
      },
      xaxis: {
        categories,
        labels: {
          style: {
            colors: chartTheme.textGrey,
            fontSize: "12px",
            fontFamily: chartTheme.fontFamily,
          },
          rotate: -30,
          hideOverlappingLabels: true,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          style: {
            colors: chartTheme.textGrey,
            fontSize: "12px",
            fontFamily: chartTheme.fontFamily,
          },
          formatter: (value: number) =>
            formatMetricValue(
              value.toString(),
              hasFloatMetrics ? "TYPE_FLOAT" : "TYPE_INTEGER",
            ),
        },
      },
      tooltip: {
        theme: chartTheme.theme,
        y: {
          formatter: (value: number) =>
            formatMetricValue(
              value.toString(),
              hasFloatMetrics ? "TYPE_FLOAT" : "TYPE_INTEGER",
            ),
          title: {
            formatter: formatMetricOrDimensionName,
          },
        },
      },
      legend: {
        show: !distributed,
        fontFamily: chartTheme.fontFamily,
        labels: { colors: chartTheme.textGrey },
        formatter: formatMetricOrDimensionName,
      },
    }),
    [chartTheme, categories, distributed, hasFloatMetrics],
  );

  return (
    <Chart
      className={styles.root}
      options={options}
      series={series}
      type="bar"
      height="100%"
    />
  );
};

const PARSERS: Record<string, (value: string) => number> = {
  TYPE_INTEGER: parseInt,
  TYPE_FLOAT: parseFloat,
  TYPE_PERCENT: (value: string) => parseFloat(value) * 100,
};
